import React, { useState } from "react";
import { memoryService, UpsertMemoryDto } from "../services/memory.service";
import { useToast } from "../context/ToastContext";
import "../styles/clients.css";

interface Props {
    clientId: string;
    onClose: () => void;
    onCreated: () => void;
}

export default function CreateKnowledgeModal({ clientId, onClose, onCreated }: Props) {
    const { showToast } = useToast();
    const [question, setQuestion] = useState("");
    const [answer, setAnswer] = useState("");
    const [category, setCategory] = useState("");
    const [isPublic, setIsPublic] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!question.trim()) {
            setError("Question is required.");
            return;
        }
        if (!answer.trim()) {
            setError("Answer is required.");
            return;
        }

        setError("");
        setLoading(true);

        try {
            const payload: UpsertMemoryDto = {
                question: question.trim(),
                answer: answer.trim(),
                clientId,
            };
            if (category) payload.category = category;
            if (isPublic) payload.isPublic = true;
            await memoryService.create(payload);
            showToast("Knowledge entry created successfully.");
            onCreated();
        } catch (err: any) {
            setError(err.response?.data?.msg || "Failed to create knowledge entry.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-card" onClick={(e) => e.stopPropagation()}>
                <h2>Add Knowledge</h2>
                <p className="modal-subtitle">Create a new Q&A entry for the knowledge base</p>

                <form className="modal-form" onSubmit={handleSubmit}>
                    {error && <div className="modal-error">{error}</div>}

                    <div className="modal-field">
                        <label htmlFor="kb-question">Question *</label>
                        <textarea
                            id="kb-question"
                            value={question}
                            onChange={(e) => setQuestion(e.target.value)}
                            placeholder="What question should the chatbot answer?"
                            autoFocus
                        />
                    </div>

                    <div className="modal-field">
                        <label htmlFor="kb-answer">Answer *</label>
                        <textarea
                            id="kb-answer"
                            value={answer}
                            onChange={(e) => setAnswer(e.target.value)}
                            placeholder="The answer the chatbot should provide"
                        />
                    </div>

                    <div className="modal-field">
                        <label htmlFor="kb-category">Category</label>
                        <input
                            id="kb-category"
                            type="text"
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                            placeholder="e.g. faq, product, policy"
                        />
                    </div>

                    <div className="modal-checkbox">
                        <input
                            id="kb-public"
                            type="checkbox"
                            checked={isPublic}
                            onChange={(e) => setIsPublic(e.target.checked)}
                        />
                        <label htmlFor="kb-public">Make visible to all clients</label>
                    </div>

                    <div className="modal-actions">
                        <button type="button" className="btn-modal-cancel" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="btn-modal-submit" disabled={loading}>
                            {loading ? "Creating..." : "Create"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
